/**
 * Поиск по уже загруженному стоку — запасной путь строки поиска, когда сервер
 * отвечает `available: false` (Meilisearch выключен или недоступен, #1344).
 *
 * ## Это не замена Meilisearch
 *
 * Ни опечаток, ни морфологии, ни ранжирования тут нет — только вхождение всех
 * слов запроса в описание машины. Но «мерседес» в стоке из четырёхсот машин
 * находится и так, а строка поиска, которая на любой запрос отвечает
 * «недоступно», выглядит сломанной витриной, а не выключенным сервисом.
 *
 * ## Почему все слова, а не любое
 *
 * «mercedes gle» должен СУЗИТЬ выдачу до одной машины, а не расширить её до
 * всех Mercedes и всех GLE разом. Поэтому условие — И по каждому слову.
 *
 * Функция чистая и лежит отдельно от компонента намеренно: правило поиска
 * проверяется тестом, а не прогоном интерфейса.
 */

/**
 * Поля, по которым ищем — имена сверены с тем, что реально приходит в сток
 * (`bodyStyle`, а не `bodyType`: на выдуманном имени запрос «suv» молча ничего
 * не находил). `description` в списке намеренно — в ней лежит комплектация
 * («1.2 MPI 79 GT LINE»), её тоже набирают.
 */
const FIELDS = [
  'make',
  'model',
  'year',
  'fuelType',
  'bodyStyle',
  'transmission',
  'color',
  'description',
];

/**
 * Синонимы кузовов. Покупатель набирает «suv», а в стоке этот кузов записан
 * «Crossover» — и запрос, который сама же строка предлагает в подсказке
 * («e.g. mercedes suv»), не находил ничего.
 *
 * Список короткий и только про кузова: это запасной путь, а не место, где
 * заводят вторую поисковую систему. Когда Meilisearch на месте, синонимами
 * занимается он.
 */
const SYNONYMS = {
  suv: ['crossover'],
  crossover: ['suv'],
  '4x4': ['crossover'],
  saloon: ['sedan'],
  sedan: ['saloon'],
  estate: ['wagon'],
  wagon: ['estate'],
  hatch: ['hatchback'],
  cabrio: ['convertible'],
  cabriolet: ['convertible'],
};

/**
 * @param {Array<object>} cars — загруженный сток.
 * @param {string} query — то, что набрали в строке.
 * @returns {Array<object>} машины, в описании которых есть ВСЕ слова запроса.
 */
export function matchCarsByQuery(cars, query) {
  const tokens = String(query ?? '')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (!tokens.length) return [];

  return (cars ?? []).filter((car) => {
    const haystack = FIELDS.map((field) => car?.[field])
      .filter((value) => value !== null && value !== undefined && value !== '')
      .join(' ')
      .toLowerCase();
    // Слово засчитано, если в описании есть оно само ИЛИ его синоним.
    return tokens.every((token) =>
      [token, ...(SYNONYMS[token] ?? [])].some((variant) =>
        haystack.includes(variant)
      )
    );
  });
}

export default matchCarsByQuery;

/**
 * Что каталог показывает по ответу поиска (#1530).
 *
 * Сервер ответил `available: false` (Meilisearch выключен или недоступен,
 * #1344): витрина фильтрует загруженный сток тем же правилом, что подсказки
 * в герое; у дилеров (mini-catalog, виджет) — ответ сервера как есть, без
 * выдуманных результатов.
 *
 * @param {object} result — ответ `searchCarInventory`
 * @param {{ isSiteCatalog: boolean, cars: Array<object>, term: string }} ctx
 * @returns {object} результат для `handleSearchResults`
 */
export function resolveCatalogSearch(result, { isSiteCatalog, cars, term }) {
  if (result && result.available === false && isSiteCatalog) {
    const hits = matchCarsByQuery(cars, term);
    return { hits, estimatedTotalHits: hits.length, offline: true };
  }
  return result;
}
